"use client";

import { EmailCaptureForm } from "@/components/forms/email-capture-form";
import { useNewsletterSubscribe } from "@/hooks/use-newsletter-subscribe";
import { cn } from "@/lib/utils";

export interface NewsletterEmailCaptureProps {
  emailPlaceholder: string;
  subscribeLabel: string;
  sendingLabel?: string;
  /** Shown in place of the form once the subscription succeeds. */
  successMessage: string;
  errorMessage?: string;
  className?: string;
  inputClassName?: string;
  formClassName?: string;
  statusClassName?: string;
  buttonLayout?: "hero-form" | "default";
  id?: string;
}

/**
 * Newsletter subscribe capture — submits to the HubSpot Insights form and
 * stays on the page, unlike ApplyNowEmailCapture which redirects to signup.
 */
export function NewsletterEmailCapture({
  emailPlaceholder,
  subscribeLabel,
  sendingLabel = "Subscribing…",
  successMessage,
  errorMessage = "Something went wrong. Please try again.",
  className,
  inputClassName,
  formClassName,
  statusClassName,
  buttonLayout = "default",
  id = "newsletter-email",
}: NewsletterEmailCaptureProps) {
  const { submit, isSubmitting, isSubscribed, hasError } =
    useNewsletterSubscribe();

  if (isSubscribed) {
    return (
      <p
        role="status"
        className={cn("text-ds-body-md text-grey-950", statusClassName)}
      >
        {successMessage}
      </p>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <EmailCaptureForm
        id={id}
        emailPlaceholder={emailPlaceholder}
        submitLabel={subscribeLabel}
        sendingLabel={sendingLabel}
        onSubmitEmail={submit}
        isSubmitting={isSubmitting}
        inputClassName={inputClassName}
        formClassName={formClassName}
        buttonLayout={buttonLayout}
      />
      {hasError ? (
        <p role="alert" className={cn("text-ds-body-sm mt-2 text-red-700")}>
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
